import React from 'react';
import { ROUTINES } from '../routines';

const LockIcon = () => (
  <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <rect x="5" y="10" width="14" height="10" rx="2" />
    <path d="M8 10V7a4 4 0 0 1 8 0v3" />
  </svg>
);

// The bold, "in play" card — whichever tier the user actually has right now
// (Free until they subscribe, Pro once they have) gets this treatment; the
// other tier is shown as a SecondaryLink instead. `badge` is optional — the
// locked Pro teaser uses it to show a "Locked" pill beside the tag.
const HighlightCard = ({ tag, tagColor, border, title, subtitle, onClick, disabled, ariaLabel, ariaHaspopup, badge, className = 'mt-4' }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-label={ariaLabel}
    aria-haspopup={ariaHaspopup}
    className={`${className} w-full text-left rounded-3xl border ${border} bg-gradient-to-br from-blue-500/15 to-violet-500/10 p-5 transition-all active:scale-[.99] disabled:cursor-wait disabled:opacity-60 hover:bg-white/[0.08]`}
  >
    <span className="flex items-center justify-between gap-3">
      <span className={`text-[0.625rem] font-bold uppercase tracking-widest ${tagColor}`}>{tag}</span>
      {badge}
    </span>
    <span className="mt-1 block text-xl font-bold">{title}</span>
    <span className="mt-1 block text-sm text-white/55">{subtitle}</span>
  </button>
);

// The muted, "not what you're currently using" link — a small reminder of
// the other tier rather than something competing for attention.
const SecondaryLink = ({ tag, tagColor, title, onClick, disabled, ariaLabel, ariaHaspopup, badge, className = 'mt-3' }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-label={ariaLabel}
    aria-haspopup={ariaHaspopup}
    className={`${className} flex w-full items-center justify-between gap-3 rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3 text-left transition-colors active:scale-[.99] disabled:cursor-wait disabled:opacity-60 hover:bg-white/[0.06]`}
  >
    <span>
      <span className={`block text-[0.625rem] font-bold uppercase tracking-widest ${tagColor}`}>{tag}</span>
      <span className="block text-sm font-bold text-white/70">{title}</span>
    </span>
    {badge || <span aria-hidden="true" className="text-white/30">→</span>}
  </button>
);

const RoutineLibrary = ({ isPro, accessPending = false, onFreeTap, onChooseRoutine, onProRequired, onSignOut }) => {
  // Once Pro access is confirmed, routines take over as the main event and
  // Free Tap steps back to a small secondary link — no need to keep selling
  // Pro to someone who already has it. While access is still resolving we
  // stay on the locked/teaser layout rather than risk flashing the Pro grid.
  const showProGrid = isPro && !accessPending;

  return (
    <main
      className="min-h-screen bg-gradient-to-b from-slate-950 via-indigo-950/30 to-black text-white px-5 pb-8 overflow-y-auto"
      style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 2rem)' }}
    >
      <div className="mx-auto max-w-2xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-indigo-300 text-xs font-bold uppercase tracking-[0.22em]">Choose your rhythm</p>
            <h1 className="mt-2 text-4xl font-extrabold">What would help right now?</h1>
          </div>
          {onSignOut && (
            <button
              type="button"
              onClick={onSignOut}
              className="mt-1 shrink-0 rounded-full bg-white/10 px-3 py-1.5 text-xs font-bold text-white/70 transition-colors hover:bg-white/15 hover:text-white active:scale-[.98]"
            >
              Sign Out
            </button>
          )}
        </div>

        {showProGrid ? (
          <>
            <SecondaryLink
              tag="Free"
              tagColor="text-white/40"
              title="Free Tap — no timer, tap at your own pace"
              onClick={onFreeTap}
              className="mt-7"
            />

            <HighlightCard
              tag="Pro"
              tagColor="text-violet-200"
              border="border-violet-300/20"
              title="Every Colour & Kids Mode"
              subtitle="Included with your subscription — find them in Settings."
              onClick={onFreeTap}
            />

            <div className="mt-6 flex items-center justify-between">
              <h2 className="text-lg font-bold">Structured routines</h2>
              <span className="rounded-full bg-violet-500/15 px-3 py-1 text-[0.625rem] font-bold uppercase tracking-widest text-violet-200">Pro</span>
            </div>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              {ROUTINES.map((routine, index) => (
                <button
                  key={routine.id}
                  type="button"
                  onClick={() => onChooseRoutine?.(routine)}
                  aria-label={`${routine.title}, ${routine.durationMinutes} minutes. Start routine.`}
                  className={`relative min-h-36 rounded-3xl border p-5 text-left transition-all active:scale-[.99] ${
                    index === 0
                      ? 'sm:col-span-2 border-blue-300/20 bg-gradient-to-br from-blue-500/15 to-violet-500/10 hover:bg-white/[0.08]'
                      : 'border-white/10 bg-white/[0.055] hover:bg-white/[0.08]'
                  }`}
                >
                  <span className="text-[0.625rem] font-bold uppercase tracking-widest text-indigo-300">{routine.durationMinutes} min</span>
                  <span className="mt-2 block text-lg font-bold">{routine.title}</span>
                  <span className="mt-2 block text-sm leading-relaxed text-white/65">{routine.description}</span>
                  <span className="mt-3 block text-[0.625rem] font-semibold uppercase tracking-wider text-indigo-200/60">{routine.paceLabel}</span>
                </button>
              ))}
            </div>
          </>
        ) : (
          <>
            <p className="mt-3 text-white/55">Free Tap is always available. Guided routines are included with TapTap Pro.</p>

            <HighlightCard
              tag="Free"
              tagColor="text-blue-200"
              border="border-blue-300/20"
              title="Free Tap"
              subtitle="Choose a timer or continue without one, then tap at your own pace."
              onClick={onFreeTap}
              className="mt-7"
            />

            <HighlightCard
              tag="Pro"
              tagColor="text-violet-200"
              border="border-violet-300/20"
              title={accessPending ? 'TapTap Pro — checking access…' : 'TapTap Pro'}
              subtitle={accessPending ? undefined : '5 guided routines — for calm, sleep, feeling overwhelmed, waking up & refocusing — plus Kids Mode and every colour.'}
              onClick={() => onProRequired?.('routines')}
              disabled={accessPending}
              ariaLabel={accessPending ? 'TapTap Pro. Checking access.' : 'TapTap Pro, locked. Open Pro details.'}
              ariaHaspopup={accessPending ? undefined : 'dialog'}
              badge={!accessPending && (
                <span className="inline-flex items-center gap-1 rounded-full border border-violet-300/15 bg-violet-500/10 px-2 py-1 text-[0.5625rem] font-bold uppercase tracking-wider text-violet-200">
                  <LockIcon /> Locked
                </span>
              )}
            />
          </>
        )}
      </div>
    </main>
  );
};

export default RoutineLibrary;
